import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, writeBatch, serverTimestamp, doc, getDocs, onSnapshot, setDoc, query, orderBy, limit } from 'firebase/firestore';
import {
    FaQrcode,
    FaSpinner,
    FaBuilding,
    FaUsers,
    FaTags,
    FaPrint,
    FaArrowRight,
    FaLayerGroup,
    FaComments
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { nanoid } from 'nanoid';

const AdminPage = () => {
    // Stats State
    const [counts, setCounts] = useState({
        users: 0,
        companies: 0,
        qrs: 0,
        unlinkedQrs: 0
    });
    const [loadingStats, setLoadingStats] = useState(true);

    // Chat & System Settings State
    const [chatEnabled, setChatEnabled] = useState(true);
    const [togglingChat, setTogglingChat] = useState(false);
    const [recentChats, setRecentChats] = useState([]);
    const [loadingChats, setLoadingChats] = useState(true);

    // QR Code Generator State
    const [numberOfCodes, setNumberOfCodes] = useState(10);
    const [prefix, setPrefix] = useState('KT');
    const [generatedCodes, setGeneratedCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Real-time listener for System Settings (Chat feature toggle)
    useEffect(() => {
        const settingsRef = doc(db, 'systemSettings', 'features');
        const unsubSettings = onSnapshot(settingsRef, (docSnap) => {
            if (docSnap.exists()) {
                setChatEnabled(docSnap.data().chatEnabled !== false);
            } else {
                setChatEnabled(true);
            }
        }, (err) => console.warn("Settings listen error:", err));

        // Real-time listener for recent QR chat conversations
        const chatsQuery = query(collection(db, 'qrChats'), orderBy('updatedAt', 'desc'), limit(12));
        const unsubChats = onSnapshot(chatsQuery, (snap) => {
            setRecentChats(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoadingChats(false);
        }, (err) => {
            console.warn("QR Chats listen error:", err);
            setLoadingChats(false);
        });

        return () => {
            unsubSettings();
            unsubChats();
        };
    }, []);

    const handleToggleChat = async () => {
        setTogglingChat(true);
        try {
            const nextVal = !chatEnabled;
            await setDoc(doc(db, 'systemSettings', 'features'), {
                chatEnabled: nextVal,
                updatedAt: serverTimestamp()
            }, { merge: true });
        } catch (err) {
            console.error("Error toggling chat:", err);
            alert("Ayar güncellenirken hata oluştu: " + err.message);
        } finally {
            setTogglingChat(false);
        }
    };

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const usersSnap = await getDocs(collection(db, 'users'));
                const users = usersSnap.docs.map(d => d.data());
                const totalUsers = users.length;
                const companiesCount = users.filter(u => u.role === 'company').length;

                const qrsSnap = await getDocs(collection(db, 'qrCodes'));
                const qrs = qrsSnap.docs.map(d => d.data());
                const totalQrs = qrs.length;
                const unlinkedCount = qrs.filter(q => !q.isLinked).length;

                setCounts({
                    users: totalUsers,
                    companies: companiesCount,
                    qrs: totalQrs,
                    unlinkedQrs: unlinkedCount
                });
            } catch (err) {
                console.error('İstatistikler alınırken hata:', err);
            } finally {
                setLoadingStats(false);
            }
        };

        fetchStats();
    }, []);

    const handleGenerateCodes = async (e) => {
        e.preventDefault();
        const num = Number(numberOfCodes);
        if (!Number.isInteger(num) || num <= 0 || num > 500) {
            return setError('Lütfen 1 ile 500 arasında geçerli bir tam sayı girin.');
        }
        setLoading(true);
        setError('');
        setSuccess('');
        setGeneratedCodes([]);

        const batch = writeBatch(db);
        const codesToGenerate = [];
        const codesCollectionRef = collection(db, 'qrCodes');

        try {
            for (let i = 0; i < num; i++) {
                const newCode = `${prefix.toUpperCase()}-${nanoid(6).toUpperCase()}`;
                const newCodeRef = doc(codesCollectionRef, newCode);
                batch.set(newCodeRef, {
                    code: newCode,
                    isLinked: false,
                    linkedUserId: null,
                    createdAt: serverTimestamp(),
                    linkedAt: null,
                    prefix: prefix.toUpperCase()
                });
                codesToGenerate.push({ id: newCode, url: `https://kodutara.web.app/q/${newCode}` });
            }
            await batch.commit();
            setGeneratedCodes(codesToGenerate);
            setSuccess(`${num} adet QR kodu başarıyla veritabanına eklendi.`);

            // Update stats
            setCounts(prev => ({
                ...prev,
                qrs: prev.qrs + num,
                unlinkedQrs: prev.unlinkedQrs + num
            }));
        } catch (err) {
            console.error("Error generating codes:", err);
            setError(`Kodlar oluşturulurken bir hata oluştu: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto space-y-8">

                {/* Dashboard Title & Overview */}
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                            Admin Yönetim Merkezi
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Kullanıcı hesapları, araçlar, şirketler ve çoklu sticker tabaka baskılarını tek noktadan yönetin.
                        </p>
                    </div>

                    <Link
                        to="/admin/print-qr"
                        className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl text-xs flex items-center gap-2 shadow-sm transition-all"
                    >
                        <FaPrint size={14} />
                        <span>Çoklu Sticker PDF Bas</span>
                    </Link>
                </div>

                {/* Live Stats Bar */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                        <span className="text-xs text-gray-500 font-medium">Toplam Kullanıcı</span>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                            {loadingStats ? <FaSpinner className="animate-spin text-sm" /> : counts.users}
                        </p>
                    </div>

                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                        <span className="text-xs text-gray-500 font-medium">Rent A Car Şirketleri</span>
                        <p className="text-2xl font-bold text-purple-600 mt-1">
                            {loadingStats ? <FaSpinner className="animate-spin text-sm" /> : counts.companies}
                        </p>
                    </div>

                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                        <span className="text-xs text-gray-500 font-medium">Toplam QR Kodu</span>
                        <p className="text-2xl font-bold text-rose-600 mt-1">
                            {loadingStats ? <FaSpinner className="animate-spin text-sm" /> : counts.qrs}
                        </p>
                    </div>

                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                        <span className="text-xs text-gray-500 font-medium">Boşta / Eşleşmemiş QR</span>
                        <p className="text-2xl font-bold text-amber-600 mt-1">
                            {loadingStats ? <FaSpinner className="animate-spin text-sm" /> : counts.unlinkedQrs}
                        </p>
                    </div>
                </div>

                {/* Navigation Modules Hub */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

                    {/* Users Management */}
                    <Link
                        to="/admin/users"
                        className="group bg-white p-6 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md hover:border-blue-300 transition-all flex flex-col justify-between"
                    >
                        <div>
                            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl mb-4 group-hover:scale-110 transition-transform">
                                <FaUsers />
                            </div>
                            <h2 className="font-bold text-lg text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                                Kullanıcı Yönetimi
                            </h2>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                Kullanıcı detayları, şifre sıfırlama, hesap dondurma, bağlı QR kodları ve araç ayarları.
                            </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-blue-600">
                            <span>Kullanıcıları İncele</span>
                            <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                    </Link>

                    {/* Companies Management */}
                    <Link
                        to="/admin/companies"
                        className="group bg-white p-6 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md hover:border-purple-300 transition-all flex flex-col justify-between"
                    >
                        <div>
                            <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center text-xl mb-4 group-hover:scale-110 transition-transform">
                                <FaBuilding />
                            </div>
                            <h2 className="font-bold text-lg text-gray-900 mb-1 group-hover:text-purple-600 transition-colors">
                                Şirket & Filo Yönetimi
                            </h2>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                Rent A Car firmaları onaylama, araç limitleri ve filo QR slotu ataması.
                            </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-purple-600">
                            <span>Şirketleri İncele</span>
                            <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                    </Link>

                    {/* QR Codes Management */}
                    <Link
                        to="/admin/qrcodes"
                        className="group bg-white p-6 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md hover:border-rose-300 transition-all flex flex-col justify-between"
                    >
                        <div>
                            <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center text-xl mb-4 group-hover:scale-110 transition-transform">
                                <FaTags />
                            </div>
                            <h2 className="font-bold text-lg text-gray-900 mb-1 group-hover:text-rose-600 transition-colors">
                                Tüm QR Kodlar
                            </h2>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                Veritabanındaki tüm QR kodları, bağlantı durumları, kullanıcı eşleşmeleri ve çoklu seçim.
                            </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-rose-600">
                            <span>QR Kodları Gör</span>
                            <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                    </Link>

                    {/* Multi-Grid Print QR */}
                    <Link
                        to="/admin/print-qr"
                        className="group bg-white p-6 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md hover:border-amber-300 transition-all flex flex-col justify-between"
                    >
                        <div>
                            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl mb-4 group-hover:scale-110 transition-transform">
                                <FaPrint />
                            </div>
                            <h2 className="font-bold text-lg text-gray-900 mb-1 group-hover:text-amber-600 transition-colors">
                                Sticker Tabaka Baskı (PDF)
                            </h2>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                A4/A3 tabaka boyutu seçerek sayfaya sığacak ızgarada çoklu sticker PDF çıktısı üretin.
                            </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-amber-600">
                            <span>Baskı Sihirbazı</span>
                            <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                    </Link>

                </div>

                {/* System Settings & Live Chat Admin Control */}
                <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-sm border border-gray-100 space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-5">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl">
                                <FaComments />
                            </div>
                            <div>
                                <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">
                                    Canlı Destek Balonu & Sistem Ayarları
                                </h2>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Kullanıcıların ve ziyaretçilerin gördüğü canlı destek balonunu tek tıkla açın veya tamamen kapatın.
                                </p>
                            </div>
                        </div>

                        {/* Interactive Apple-Style Master Toggle Switch */}
                        <div className="flex items-center gap-3 bg-gray-50 p-2 rounded-2xl border border-gray-200">
                            <div className="text-right">
                                <span className="text-xs font-bold block text-gray-900">
                                    {chatEnabled ? 'Destek Aktif' : 'Destek Kapalı'}
                                </span>
                                <span className={`text-[10px] font-semibold ${chatEnabled ? 'text-emerald-600' : 'text-gray-400'}`}>
                                    {chatEnabled ? 'Tüm kullanıcılarda görünür' : 'Kullanıcılardan gizlendi'}
                                </span>
                            </div>

                            <button
                                type="button"
                                onClick={handleToggleChat}
                                disabled={togglingChat}
                                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                                    chatEnabled ? 'bg-emerald-500' : 'bg-gray-300'
                                }`}
                                title={chatEnabled ? "Canlı desteği kapat" : "Canlı desteği aç"}
                            >
                                <span
                                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                                        chatEnabled ? 'translate-x-7' : 'translate-x-1'
                                    }`}
                                >
                                    {togglingChat && (
                                        <FaSpinner className="animate-spin text-gray-400 text-xs m-1" />
                                    )}
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* QR Code Conversations Feed */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1.5">
                                <FaComments className="text-blue-600" />
                                <span>Karekod Sohbet Akışı ({recentChats.length})</span>
                            </h3>
                            <span className="text-[11px] text-gray-400">Canlı Güncellenir</span>
                        </div>

                        {loadingChats ? (
                            <div className="py-8 text-center text-gray-400 text-xs">
                                <FaSpinner className="animate-spin inline-block mr-1.5" /> Mesajlar yükleniyor...
                            </div>
                        ) : recentChats.length === 0 ? (
                            <div className="py-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200 text-xs text-gray-500">
                                Henüz herhangi bir karekod mesajlaşması başlatılmamış.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {recentChats.map((chat) => (
                                    <div 
                                        key={chat.id} 
                                        className="p-4 rounded-2xl border border-gray-100 bg-gray-50/70 hover:bg-white hover:border-gray-200 transition-all flex flex-col justify-between"
                                    >
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-full bg-black text-white text-[10px] font-bold flex items-center justify-center">
                                                    {chat.lastSender ? chat.lastSender.slice(0, 1).toUpperCase() : 'Z'}
                                                </div>
                                                <div>
                                                    <span className="text-xs font-bold text-gray-900 block truncate max-w-[160px]">
                                                        {chat.lastSender || 'Ziyaretçi'}
                                                    </span>
                                                    <span className="text-[10px] text-gray-400 block font-mono">
                                                        QR: {chat.id} {chat.vehiclePlate ? `• ${chat.vehiclePlate}` : ''}
                                                    </span>
                                                </div>
                                            </div>

                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                                chat.lastSenderRole === 'owner' ? 'bg-amber-100 text-amber-800' : 'bg-blue-50 text-blue-700'
                                            }`}>
                                                {chat.lastSenderRole === 'owner' ? 'Araç Sahibi' : 'Ziyaretçi'}
                                            </span>
                                        </div>

                                        <p className="text-xs text-gray-700 bg-white p-2.5 rounded-xl border border-gray-100 line-clamp-2">
                                            "{chat.lastMessage || 'Mesaj yok'}"
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Fast QR Code Generator Widget */}
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-4">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <FaQrcode className="text-blue-600" />
                                <span>Hızlı QR Kod Oluşturucu</span>
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Belirlediğiniz ön ek ile sisteme tek tıkla boşta yeni QR kodları tanımlayın.
                            </p>
                        </div>

                        <Link
                            to="/admin/print-qr"
                            className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-1"
                        >
                            <FaLayerGroup />
                            <span>Doğrudan Baskı Sayfasında Üret →</span>
                        </Link>
                    </div>

                    <form onSubmit={handleGenerateCodes} className="p-5 border rounded-xl bg-gray-50 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="prefix" className="block text-xs font-semibold text-gray-700 mb-1">
                                    Kod Ön Eki (Prefix)
                                </label>
                                <input
                                    type="text"
                                    id="prefix"
                                    value={prefix}
                                    onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                                    maxLength={5}
                                    placeholder="KT"
                                    className="w-full px-3 py-2 border rounded-xl bg-white text-sm font-mono uppercase focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                            </div>

                            <div>
                                <label htmlFor="numberOfCodes" className="block text-xs font-semibold text-gray-700 mb-1">
                                    Adet (1 - 500)
                                </label>
                                <input
                                    type="number"
                                    id="numberOfCodes"
                                    value={numberOfCodes}
                                    onChange={(e) => setNumberOfCodes(e.target.value)}
                                    min="1"
                                    max="500"
                                    className="w-full px-3 py-2 border rounded-xl bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    required
                                />
                            </div>

                            <div className="flex items-end">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-xs flex items-center justify-center gap-2 shadow-sm transition-all disabled:opacity-50"
                                >
                                    {loading ? <FaSpinner className="animate-spin" /> : <FaQrcode />}
                                    <span>{numberOfCodes} Adet Kod Oluştur</span>
                                </button>
                            </div>
                        </div>

                        {error && <p className="text-red-600 text-xs font-medium">{error}</p>}
                        {success && <p className="text-green-700 text-xs font-medium">{success}</p>}
                    </form>

                    {/* Display freshly generated codes */}
                    {generatedCodes.length > 0 && (
                        <div className="space-y-4 pt-4 border-t">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-bold text-gray-800">
                                    Son Oluşturulan Kodlar ({generatedCodes.length})
                                </h3>
                                <Link
                                    to={`/admin/print-qr?selectedCodes=${generatedCodes.map(c => c.id).join(',')}`}
                                    className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-sm"
                                >
                                    <FaPrint size={11} />
                                    <span>Bu Kodları Tabakaya Bas</span>
                                </Link>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
                                {generatedCodes.map(code => (
                                    <div key={code.id} className="border p-3 rounded-xl text-center bg-white shadow-xs">
                                        <QRCodeSVG value={code.url} size={96} className="mx-auto" />
                                        <p className="font-mono font-bold text-xs mt-2 text-gray-800 break-all">{code.id}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default AdminPage;